import React from 'react';
import { Settings, Usb, AlertCircle, RotateCcw, Network as NetworkIcon, Wifi, WifiOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// Reusable USN-sticker printer setup dialog. Driven entirely by the
// useUsnStickerPrint hook so Folding Print & Segregation share one setup.
export const UsnPrinterSettingsDialog = ({ open, onOpenChange, print }) => {
  const {
    settings, updateSetting, labelConfigs, loadingConfigs,
    printerConnected, usbSupported, connectingPrinter, connectPrinter, forgetPrinter,
    networkActive, opRoute,
  } = print;

  const handleConnect = async () => {
    try {
      const name = await connectPrinter();
      toast.success(`Printer connected: ${name}`);
    } catch (err) {
      toast.error(err.message || 'Failed to connect printer');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="printer-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="w-5 h-5" /> Printer Setup</DialogTitle>
          <DialogDescription>Configured once. Applied automatically to every print.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {networkActive && (
            <div data-testid="network-station-panel">
              <Label className="text-sm font-medium flex items-center gap-1.5">
                <NetworkIcon className="w-4 h-4" /> Network Print Station
              </Label>
              <div className="mt-1 border rounded-lg p-3 space-y-1.5 bg-emerald-50/60 border-emerald-200">
                {opRoute?.assigned && opRoute?.route ? (
                  opRoute.route.exists && opRoute.route.online ? (
                    <div className="flex items-center gap-2 text-emerald-800">
                      <Wifi className="w-4 h-4 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {opRoute.route.agent_name || 'Assigned station'}
                          {opRoute.route.printer_name ? ` · ${opRoute.route.printer_name}` : ''}
                        </p>
                        <p className="text-xs text-emerald-700 truncate">Prints automatically, no per-device setup.</p>
                      </div>
                    </div>
                  ) : opRoute.route.exists ? (
                    <div className="flex items-start gap-2 text-amber-700">
                      <WifiOff className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate text-amber-800">
                          {opRoute.route.agent_name || 'Assigned station'}
                          {opRoute.route.printer_name ? ` · ${opRoute.route.printer_name}` : ''}
                        </p>
                        <p className="text-xs">Station is offline. Start its Print Bridge app — jobs print once online.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-amber-700">
                      <WifiOff className="w-4 h-4 shrink-0" />
                      <p className="text-xs flex-1">The assigned station no longer exists. Ask an admin to re-assign it in Print Stations → Operation Routing.</p>
                    </div>
                  )
                ) : (
                  <p className="text-xs text-gray-600">
                    Network mode is on but this operation isn&apos;t routed to a station. Pick one from the USB / Network switch, or ask an admin to assign it.
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <Label className="text-sm font-medium">{networkActive ? 'USB Printer (fallback)' : 'USB Printer'}</Label>
            <div className="mt-1 border rounded-lg p-3 bg-gray-50 space-y-2">
              {settings.printerVendorId && printerConnected ? (
                <div className="flex items-center gap-2">
                  <Usb className="w-4 h-4 text-green-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{settings.printerName || 'Printer'}</p>
                    <p className="text-xs text-gray-500">VID: 0x{settings.printerVendorId?.toString(16)} · PID: 0x{settings.printerProductId?.toString(16)}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={forgetPrinter} data-testid="forget-printer-btn">
                    <RotateCcw className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ) : settings.printerVendorId ? (
                <div className="flex items-center gap-2 text-amber-700">
                  <AlertCircle className="w-4 h-4" />
                  <p className="text-xs flex-1">Saved printer not found. Click Connect to re-authorize.</p>
                </div>
              ) : (
                <p className="text-xs text-gray-500">No printer connected.</p>
              )}
              <Button
                variant="default"
                size="sm"
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleConnect}
                disabled={connectingPrinter || !usbSupported}
                data-testid="connect-printer-btn"
              >
                <Usb className="w-4 h-4 mr-1.5" />
                {connectingPrinter ? 'Connecting...' : (printerConnected ? 'Re-connect Printer' : 'Connect Printer')}
              </Button>
              {!usbSupported && (
                <p className="text-xs text-red-600">Web USB is only supported on Chrome / Edge desktop.</p>
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Label Configuration</Label>
            <Select value={settings.configId} onValueChange={(v) => updateSetting('configId', v)}>
              <SelectTrigger className="mt-1" data-testid="label-config-select">
                <SelectValue placeholder={loadingConfigs ? 'Loading...' : 'Select label config'} />
              </SelectTrigger>
              <SelectContent>
                {labelConfigs.map((c) => (
                  <SelectItem key={c.config_id} value={c.config_id}>
                    {c.config_name} ({c.label_width_mm}×{c.label_height_mm}mm){c.section ? ` · ${c.section}` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-medium">Printer Language</Label>
              <Select value={settings.printerLanguage} onValueChange={(v) => updateSetting('printerLanguage', v)}>
                <SelectTrigger className="mt-1" data-testid="printer-lang-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tspl">TSPL (TSC)</SelectItem>
                  <SelectItem value="zpl">ZPL (Zebra)</SelectItem>
                  <SelectItem value="escpos">ESC/POS (Epson)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium">Copies per USN</Label>
              <Input
                type="number"
                min={1}
                max={10}
                value={settings.copies}
                onChange={(e) => updateSetting('copies', parseInt(e.target.value, 10) || 1)}
                className="mt-1"
                data-testid="copies-input"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} data-testid="settings-done-btn" className="w-full">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UsnPrinterSettingsDialog;
